import { Row } from "@tanstack/react-table";
import { FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";

import AvatarText from "@/components/reusables/AvatarText";
import { CustomDropdown } from "@/components/reusables/custom-action-button";
import { CustomTable } from "@/components/reusables/data-table/CustomTable";
import { Checkbox } from "@/components/ui/checkbox";
import { SELECTOR_ID } from "@/constants/global-constants";
import { ROUTES } from "@/constants/routes_sidebar/routes-constant";
import { ToolsConstants } from "@/constants/tools.constant";
import useGetFilter from "@/hooks/useGetFilter";
import { ResponsePagination } from "@/hooks/usePagination";
import { GlobalRowDataTypes } from "@/interfaces/global-table.interface";
import { ExtendedColumnDef } from "@/interfaces/globals";
import { IAwaitingRequest } from "@/interfaces/tools.interface";
import { cn } from "@/lib/utils";

interface IAwaitingRequestTableProps {
  data: IAwaitingRequest[];
  pagination: ResponsePagination;
}

const AwaitingRequestTable = ({
  data,
  pagination,
}: IAwaitingRequestTableProps): JSX.Element => {
  const navigate = useNavigate();
  const { AWAITING } = ToolsConstants.REQUEST;
  const { projectList } = useGetFilter(["project"]);

  const handleNavigate = (id: string): void => {
    navigate(ROUTES.TOOLS + ROUTES.AWAITING + "/" + id);
  };

  const getActionOptions = (
    row: Row<IAwaitingRequest>,
  ): ActionButtonDataTypes[] => [
    {
      icon: FileText,
      name: "View Products",
      onclick: () => handleNavigate(row.original.id),
    },
  ];

  const columns: ExtendedColumnDef<IAwaitingRequest>[] = [
    {
      map: {
        key: "id",
        valueFrom: "id",
      },
      id: SELECTOR_ID,
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label='Select all'
          className={cn(
            "hidden group-hover/header:block",
            table.getIsSomePageRowsSelected() && "block",
            table.getIsAllPageRowsSelected() && "!block",
          )}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label='Select row'
          onClick={(e) => e.stopPropagation()}
        />
      ),

      size: 12,
      enableSorting: false,
      enableHiding: false,
    },
    {
      map: {
        key: "Project",
        dataTransformer: (data) => data?.name || "-",
      },
      accessorKey: "Project",
      header: "Project",
      cell: ({ row }) => row.original?.name,
      meta: {
        minWidth: 200,
        showRightBorder: true,
      },
    },
    {
      map: {
        key: "Customer",
        dataTransformer: (data) => data?.customer?.name || "-",
      },
      accessorKey: "Customer",
      header: "Customer",
      cell: ({ row }): JSX.Element => {
        return (
          <AvatarText
            src={row?.original?.customer?.logo}
            name={`${row.original.customer?.name}`}
            title={`${row.original.customer?.name}`}
            classNames={{
              titleClassName: "line-clamp-1 text-wrap",
            }}
          />
        );
      },
      meta: {
        minWidth: 200,
        showRightBorder: true,
      },
    },
    {
      map: {
        key: "Total Products ",
        dataTransformer: (data) =>
          `${
            data?.productAssignmentBatch?.reduce(
              (sum, item) => sum + (item._count?.productAssignments ?? 0),
              0,
            ) ?? "-"
          }`,
      },
      accessorKey: "TotalProducts",
      header: "Total Products",
      cell: ({ row }) =>
        row.original?.productAssignmentBatch?.reduce(
          (sum, item) => sum + (item._count?.productAssignments ?? 0),
          0,
        ) ?? "-",
      meta: {
        minWidth: 200,
        showRightBorder: true,
      },
    },
    {
      map: {
        key: "Site Assigned",
        dataTransformer: (data) => data?.address?.label || "-",
      },
      accessorKey: "SiteAssigned",
      header: "Site Assigned",
      cell: ({ row }) => row.original?.address?.label ?? "-",
      meta: {
        minWidth: 200,
      },
    },
    {
      id: "actions",
      cell: ({ row }): React.JSX.Element => (
        <CustomDropdown data={getActionOptions(row)} />
      ),
      meta: {
        classNameRow: "w-14",
      },
    },
  ];

  return (
    <div>
      <CustomTable
        title={AWAITING.TITLE}
        columns={columns}
        data={data}
        empty={{}}
        functions={{
          search: AWAITING.TITLE,
          isExport: true,
          columnToggle: true,
          pagination,
          onRowClick: handleNavigate,
          RightClickData: (
            row: GlobalRowDataTypes<IAwaitingRequest>,
          ): ActionButtonDataTypes[] =>
            getActionOptions(row as Row<IAwaitingRequest>),
        }}
        filters={{
          dateRange: true,
          projectId: { label: "Project", options: projectList },
        }}
        hide={{
          filters: true,
          functions: true,
        }}
      />
    </div>
  );
};

export default AwaitingRequestTable;
